import {
	EventIdentifier,
	FeatureTeamEvent,
	type FetchSocialMarketingBannersV2ParamsPageDisplay,
	type SocialMarketingBannerList,
	TrackingEventType,
	useAnalytics,
	useLocale,
	useSettings
} from '@capsicum/common';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { match, P } from 'ts-pattern';
import { useDebouncedCallback } from 'use-debounce';

import { useMarketingBannersQuery } from '../data-access';
import {
	MARKETING_BANNER_SLIDE_INTERVAL,
	type MarketingBannerDetails,
	MarketingBannerImageTypes,
	MarketingBannerThemes
} from '../models';

type UseMarketingBannerCarousel = {
	page: FetchSocialMarketingBannersV2ParamsPageDisplay;
};

export const useMarketingBannerCarousel = ({ page }: UseMarketingBannerCarousel) => {
	const { locale } = useLocale();
	const analytics = useAnalytics();
	const [activeSlide, setActiveSlide] = useState(0);
	const [viewedBannerIDs, setViewedBannerIDs] = useState<Array<string>>([]);

	const {
		settings: { isMarketingBannerEnabled, isPoblanoEnabled }
	} = useSettings();

	const { data, error, isLoading, refetch } = useMarketingBannersQuery({
		query: { 'details[]': ['marketingBannerImages', 'marketingBannerTranslations'], pageDisplay: page }
	});

	useEffect(() => {
		refetch();
	}, [
		refetch,
		locale // Refetch when locale changes to update banner's translations.
	]);

	const { clickSegmentEventID, viewSegmentEventID } = useMemo(() => {
		return match(page)
			.with('dapp', () => ({
				clickSegmentEventID: EventIdentifier.BannerClickDapps,
				viewSegmentEventID: EventIdentifier.BannerViewDapps
			}))
			.with('wallet', () => ({
				clickSegmentEventID: EventIdentifier.BannerClickWallet,
				viewSegmentEventID: EventIdentifier.BannerViewWallet
			}))
			.otherwise(() => ({
				clickSegmentEventID: null,
				viewSegmentEventID: null
			}));
	}, [page]);

	const triggerViewSegmentEvent = useDebouncedCallback((bannerID: string) => {
		if (!viewSegmentEventID || viewedBannerIDs.includes(bannerID)) return;

		analytics.sendEvent(TrackingEventType.track, viewSegmentEventID, {
			feature_team: FeatureTeamEvent.ORFT,
			text: bannerID
		});

		setViewedBannerIDs(viewedBannerIDs => [...viewedBannerIDs, bannerID]);
	}, MARKETING_BANNER_SLIDE_INTERVAL / 2);

	const triggerClickSegmentEvent = useCallback(
		(bannerID: string) => {
			if (!clickSegmentEventID) return;

			analytics.sendEvent(TrackingEventType.track, clickSegmentEventID, {
				feature_team: FeatureTeamEvent.ORFT,
				text: bannerID
			});
		},
		[analytics, clickSegmentEventID]
	);

	const parseTranslations = useCallback((banner: SocialMarketingBannerList) => {
		return banner.translations?.reduce<Record<string, string>>((translations, translation) => {
			const { field, value } = translation;

			if (!field || !value) return translations;

			translations[field] = value;

			return translations;
		}, {});
	}, []);

	const marketingBanners = useMemo(
		() =>
			!data
				? []
				: data.reduce<Array<MarketingBannerDetails>>((marketingBanners, marketingBanner) => {
						const {
							bannerBody,
							bannerTheme,
							drawerBackgroundColor,
							id,
							images,
							link,
							textButton,
							title,
							topSubTitle
						} = marketingBanner;

						const isCustomBanner = bannerTheme === MarketingBannerThemes.CUSTOM;
						const drawerBackgroundImage =
							isCustomBanner &&
							images?.find(
								image =>
									image.imageType?.type ===
									MarketingBannerImageTypes.SOCIAL_MARKETING_BANNER_DRAWER_BACKGROUND
							);
						const getImage = () => {
							if (isCustomBanner) {
								return images?.find(
									image =>
										image.imageType?.type ===
										MarketingBannerImageTypes.SOCIAL_MARKETING_BANNER_BACKGROUND
								);
							}

							return Array.isArray(images) ? images[0] : null;
						};
						const hasRequiredTextFields = title != null && bannerBody != null;
						const image = getImage();
						const translations = parseTranslations(marketingBanner);

						const action: MarketingBannerDetails['action'] = match(marketingBanner)
							.with({ drawer: true }, () => {
								if (
									!isCustomBanner &&
									(!marketingBanner.drawerBody ||
										!marketingBanner.drawerPrimaryButtonText ||
										!marketingBanner.drawerPrimaryButtonLink)
								) {
									return;
								} else if (
									isCustomBanner &&
									(!drawerBackgroundColor || !drawerBackgroundImage || !drawerBackgroundImage.url)
								) {
									return;
								}

								return {
									backgroundColor: drawerBackgroundColor,
									body: translations?.drawerBody ?? marketingBanner.drawerBody,
									image:
										drawerBackgroundImage && drawerBackgroundImage.url
											? {
													blurHash: drawerBackgroundImage.blurHash,
													url: drawerBackgroundImage.url
												}
											: undefined,
									primary: {
										label:
											translations?.drawerPrimaryButtonText ??
											marketingBanner.drawerPrimaryButtonText,
										url: marketingBanner.drawerPrimaryButtonLink
									},
									secondary:
										!marketingBanner.drawerSecondaryButtonText ||
										!marketingBanner.drawerSecondaryButtonLink
											? undefined
											: {
													label:
														translations?.drawerSecondaryButtonText ??
														marketingBanner.drawerSecondaryButtonText,
													url: marketingBanner.drawerSecondaryButtonLink
												},
									type: 'modal' as const
								};
							})
							.with({ link: P.when(value => !!value) }, () => ({
								onTrigger: () => triggerClickSegmentEvent(marketingBanner.id),
								type: 'link' as const,
								url: link!
							}))
							.otherwise(() => undefined);

						if (bannerTheme !== null && ((isCustomBanner && image) || hasRequiredTextFields)) {
							marketingBanners.push({
								action,
								body: translations?.bannerBody ?? bannerBody,
								cta: translations?.textButton ?? textButton,
								header: translations?.title ?? title,
								id,
								image: image as MarketingBannerDetails['image'],
								subtitle: translations?.topSubTitle ?? topSubTitle,
								theme: bannerTheme as MarketingBannerThemes
							});
						}

						return marketingBanners;
					}, []),
		[data, parseTranslations, triggerClickSegmentEvent]
	);

	useEffect(() => {
		const activeMarketingBannerID = marketingBanners[activeSlide]?.id;

		if (activeMarketingBannerID) triggerViewSegmentEvent(activeMarketingBannerID);
	}, [activeSlide, marketingBanners, triggerViewSegmentEvent]);

	return {
		activeSlide,
		isLoading,
		isVisible: isPoblanoEnabled && isMarketingBannerEnabled && !error,
		marketingBanners,
		onActiveSlideChange: setActiveSlide,
		refetch,
		totalAmount: marketingBanners.length
	};
};
